import { useEffect, useState, useCallback } from 'react';
import { DRAFT_KEY_PREFIX } from './useFormDraft';
import { formatDistanceToNow } from 'date-fns';

const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface DraftItem {
  key: string;
  formId: string;
  savedAt: number;
  ageLabel: string;
  label: string;
  route: string;
}

/**
 * Maps a formId (e.g. "transaction-create", "budget-edit-abc123") to a
 * human-readable label and the page route where the form lives.
 */
function parseDraft(formId: string): { label: string; route: string } {
  if (formId.startsWith('transaction')) {
    return {
      label: formId === 'transaction-create' ? 'New Transaction' : 'Edit Transaction',
      route: '/transactions',
    };
  }
  if (formId.startsWith('budget')) {
    return {
      label: formId === 'budget-create' ? 'New Budget' : 'Edit Budget',
      route: '/budgets',
    };
  }
  if (formId.startsWith('goal')) {
    return {
      label: formId === 'goal-create' ? 'New Goal' : 'Edit Goal',
      route: '/goals',
    };
  }
  if (formId.startsWith('account')) {
    return {
      label: formId === 'account-create' ? 'New Account' : 'Edit Account',
      route: '/accounts',
    };
  }
  if (formId.startsWith('category')) {
    return { label: 'Category Draft', route: '/categories' };
  }
  return { label: formId, route: '/' };
}

/**
 * [T-FAB] Returns all active draft items from localStorage for the current user.
 * Includes label, age and route for each draft.
 */
export function useDraftList(userId: string | undefined): {
  drafts: DraftItem[];
  discard: (key: string) => void;
  refresh: () => void;
} {
  const [drafts, setDrafts] = useState<DraftItem[]>([]);

  const scan = useCallback(() => {
    if (!userId) {
      setDrafts([]);
      return;
    }
    const found: DraftItem[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (!key.startsWith(DRAFT_KEY_PREFIX)) continue;
        if (!key.endsWith(`:${userId}`)) continue;

        const raw = localStorage.getItem(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (
            typeof parsed.savedAt !== 'number' ||
            Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS
          ) {
            continue;
          }
          // Extract formId: key format is "draft:<formId>:<userId>"
          const withoutPrefix = key.slice(DRAFT_KEY_PREFIX.length);
          const formId = withoutPrefix.slice(0, withoutPrefix.lastIndexOf(':'));
          const { label, route } = parseDraft(formId);
          found.push({
            key,
            formId,
            savedAt: parsed.savedAt,
            ageLabel: formatDistanceToNow(new Date(parsed.savedAt), { addSuffix: true }),
            label,
            route,
          });
        } catch {
          // ignore malformed entries
        }
      }
    } catch {
      // ignore localStorage access errors
    }
    found.sort((a, b) => b.savedAt - a.savedAt);
    setDrafts(found);
  }, [userId]);

  const discard = useCallback(
    (key: string) => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
      scan();
    },
    [scan]
  );

  useEffect(() => {
    scan();
    const interval = setInterval(scan, 30_000);
    return () => clearInterval(interval);
  }, [scan]);

  return { drafts, discard, refresh: scan };
}
